/**
 * Research v2, phase 2: scientists ("vezető tudósok") and survey data as research-point sources
 * (docs/research-design.md §11). Everything here is pure and deterministic: the candidate offer is a function of
 * (world seed, civilization, offer number), so it cannot be re-rolled by reloading.
 */
import { z } from 'zod';
import { hashString } from './research-labs.js';
import { seededRng } from './research-draw.js';
import { generateName, culturesOf, DEFAULT_SPECIES, SPECIES_IDS, type SpeciesId } from './research-names.js';
import { TECH_AREAS, type TechArea } from './research-catalog.js';

export const SCIENTIST_TRAITS = ['gifted', 'expert', 'quick_learner', 'frugal', 'explorer'] as const;
export type ScientistTrait = (typeof SCIENTIST_TRAITS)[number];
/** Trait texts live in the catalog: `research.trait.<id>.name` / `.blurb`. */
export const traitKey = (trait: ScientistTrait, part: 'name' | 'blurb') => `research.trait.${trait}.${part}`;

export const THEMES = ['economy', 'military', 'exploration', 'diplomacy', 'science'] as const;

export const LEADER_RULES = {
  /** Real hours between two offers (a new set of candidates). */
  offerRefreshH: 24,
  offerSize: 4,
  maxLevel: 5,
  /** XP needed to reach level n (index = level). */
  xpForLevel: [0, 0, 40, 120, 280, 600] as readonly number[],
  /** Real days a scientist works before retiring: 30..60 (by id hash). */
  lifespanDays: { min: 30, max: 60 },
  baseSlots: 3,
  maxSlots: 9,
  /** RP bonus of the area's lead scientist: base + per level. */
  rpBase: 0.04,
  rpPerLevel: 0.02,
  /** Weight of the cards of the expert's theme: 1 + this × level. */
  expertCardPerLevel: 0.15,
  hire: { metalPerLevel: 350, energyPerLevel: 200 },
  upkeep: { base: 8, perLevel: 6 },
} as const;

export const SURVEY_DATA = {
  /** RP of one surveyed system: base + richness × factor, before the diminishing returns. */
  base: 4, perRichness: 0.12, maxPerSystem: 16,
  /** Each earlier claim of the civilization lowers the yield by this much, down to the floor. */
  decay: 0.02, floor: 0.3,
  /** The most survey RP a civilization can ever collect. */
  totalCap: 600,
} as const;

export interface ScientistLike { level: number; trait: ScientistTrait; focus: string | null }

/** Traits a species can have (phase 1-2: everybody has them all; the field is prepared for species-bound traits). */
export const SPECIES_TRAITS: Record<SpeciesId, readonly ScientistTrait[]> = { human: SCIENTIST_TRAITS, vorr: SCIENTIST_TRAITS, selu: SCIENTIST_TRAITS };

export interface Candidate {
  slot: number; speciesId: SpeciesId; cultureId: string; nameParts: string[]; name: string; area: TechArea; trait: ScientistTrait; focus: string | null; level: number;
  cost: { metal: number; energy: number }; upkeepEnergy: number;
}

export const levelFromXp = (xp: number): number => {
  let l = 1;
  for (let i = 2; i <= LEADER_RULES.maxLevel; i++) if (xp >= LEADER_RULES.xpForLevel[i]!) l = i;
  return l;
};
export const xpToNext = (xp: number): number | null => {
  const l = levelFromXp(xp);
  return l >= LEADER_RULES.maxLevel ? null : LEADER_RULES.xpForLevel[l + 1]! - xp;
};

export const upkeepEnergy = (level: number, trait: ScientistTrait): number =>
  Math.round((LEADER_RULES.upkeep.base + LEADER_RULES.upkeep.perLevel * level) * (trait === 'frugal' ? 0.6 : 1));
export const hireCost = (level: number) => ({ metal: LEADER_RULES.hire.metalPerLevel * level, energy: LEADER_RULES.hire.energyPerLevel * level });

/** XP a finished research gives to the scientists working in its area. */
export const xpForResearch = (tier: number, trait: ScientistTrait): number => Math.round(tier * 10 * (trait === 'quick_learner' ? 1.5 : 1));

/** The lead's RP multiplier bonus (0.1 = +10%). */
export const leaderRpBonus = (s: ScientistLike): number => (LEADER_RULES.rpBase + LEADER_RULES.rpPerLevel * s.level) * (s.trait === 'gifted' ? 1.5 : 1);

/** Card weight multipliers per theme from experts (used by the draw as the "expertise" modifier). */
export function expertiseThemes(leads: ScientistLike[]): Record<string, number> {
  const out: Record<string, number> = {};
  for (const s of leads) if (s.trait === 'expert' && s.focus) out[s.focus] = (out[s.focus] ?? 1) + LEADER_RULES.expertCardPerLevel * s.level;
  return out;
}

export const offerSeqAt = (nowMs: number, civCreatedMs: number): number =>
  Math.max(0, Math.floor((nowMs - civCreatedMs) / (LEADER_RULES.offerRefreshH * 3_600_000)));

/** The candidates of offer `offerSeq`: deterministic, the same on every call. */
export function generateCandidates(worldSeed: string, civId: string, offerSeq: number, maxLevel = 3, speciesId: SpeciesId = DEFAULT_SPECIES): Candidate[] {
  const rng = seededRng(`${worldSeed}:${civId}:scientists:${offerSeq}`);
  const out: Candidate[] = [];
  for (let slot = 0; slot < LEADER_RULES.offerSize; slot++) {
    const area = TECH_AREAS[(slot + offerSeq) % 3]!; // every offer covers all areas
    const traits = SPECIES_TRAITS[speciesId];
    const trait = traits[Math.floor(rng() * traits.length)]!;
    const level = 1 + Math.floor(rng() * Math.min(maxLevel, LEADER_RULES.maxLevel));
    const cultures = culturesOf(speciesId);
    const cultureId = cultures[Math.floor(rng() * cultures.length)]!;
    const name = generateName(speciesId, cultureId, rng);
    out.push({
      slot, speciesId, cultureId, nameParts: name.parts, name: name.display, area, trait, level, focus: trait === 'expert' ? THEMES[Math.floor(rng() * THEMES.length)]! : null,
      cost: hireCost(level), upkeepEnergy: upkeepEnergy(level, trait),
    });
  }
  return out;
}

/** Days until retirement, 30..60 by the scientist's id (deterministic, no extra storage). */
export const lifespanDaysOf = (id: string): number =>
  LEADER_RULES.lifespanDays.min + (hashString(id) % (LEADER_RULES.lifespanDays.max - LEADER_RULES.lifespanDays.min + 1));

/** Capacity: base + one per laboratory, capped. */
export const scientistCapacity = (labs: number): number => Math.min(LEADER_RULES.maxSlots, LEADER_RULES.baseSlots + labs);

/** RP of one surveyed system, with diminishing returns and the lifetime cap. */
export function surveyDataRp(richness: number, claimsSoFar: number, totalSoFar: number): number {
  const raw = Math.min(SURVEY_DATA.maxPerSystem, SURVEY_DATA.base + richness * SURVEY_DATA.perRichness);
  const factor = Math.max(SURVEY_DATA.floor, 1 - SURVEY_DATA.decay * claimsSoFar);
  return Math.max(0, Math.min(Math.round(raw * factor), SURVEY_DATA.totalCap - totalSoFar));
}

// ---- API shapes ----
export const ScientistClientSchema = z.object({
  id: z.string().uuid(), name: z.string(), nameParts: z.array(z.string()), speciesId: z.string(), cultureId: z.string(), area: z.enum(TECH_AREAS), trait: z.enum(SCIENTIST_TRAITS),
  focus: z.string().nullable(), level: z.number().int(), xp: z.number().int(), xpToNext: z.number().int().nullable(),
  assignedArea: z.enum(TECH_AREAS).nullable(), upkeepEnergy: z.number().int(), rpBonus: z.number(), retiresAt: z.string().datetime(),
});
export const CandidateClientSchema = z.object({
  offerSeq: z.number().int(), slot: z.number().int(), name: z.string(), nameParts: z.array(z.string()), speciesId: z.string(), cultureId: z.string(), area: z.enum(TECH_AREAS), trait: z.enum(SCIENTIST_TRAITS),
  focus: z.string().nullable(), level: z.number().int(), cost: z.object({ metal: z.number().int(), energy: z.number().int() }), upkeepEnergy: z.number().int(),
});
export const ScientistsStateSchema = z.object({
  enabled: z.boolean(), surveyDataEnabled: z.boolean(),
  scientists: z.array(ScientistClientSchema), candidates: z.array(CandidateClientSchema),
  capacity: z.number().int(), offerSeq: z.number().int(), nextOfferAt: z.string().datetime(),
  upkeepEnergyPerHour: z.number().int(),
  surveyData: z.object({ total: z.number().int(), cap: z.number().int(), byArea: z.record(z.string(), z.number().int()) }),
});
export { SPECIES_IDS };
export type ScientistsState = z.infer<typeof ScientistsStateSchema>;
export type ScientistClient = z.infer<typeof ScientistClientSchema>;
export const HireScientistSchema = z.object({ offerSeq: z.number().int().min(0).max(100000), slot: z.number().int().min(0).max(LEADER_RULES.offerSize - 1), idempotencyKey: z.string().min(1).max(128) });
export const AssignScientistSchema = z.object({ scientistId: z.string().uuid(), area: z.enum(TECH_AREAS).nullable() });
